import { useState } from 'react';
import * as R from 'ramda';

import NewWorkOrderIcon from '@atom/components/common/svgIcons/NewWorkOrderIcon';
import { Icon, IconButton } from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { MapData } from '@atom/types/map';
import { pluralize } from '@atom/utilities/stringUtilities';

import AssetListItem from './AssetListItem';
import ListHeader from './ListHeader';
import { filterById, sortAssets } from './listUtilities';
import NoItemsPane from './NoItemsPane';
import UserListItem from './UserListItem';
import WorkListItem from './WorkListItem';

import classes from './list.module.css';

type BulkCreateConfig = {
  canCreate: boolean;
  onCreate: () => void;
};

type ExclusionConfig = {
  onExclude: (id: string) => void;
  excludedCount: number;
};

type MapItemListProps = {
  data: MapData;
  identifyIds?: string[] | null;
  onSelectAsset: (id: string) => void;
  onSelectWork?: (id: string) => void;
  onSelectUser?: (id: string) => void;
  onHover?: (id: string | null) => void;
  onCloseIdentify?: () => void;
  bulkCreate?: BulkCreateConfig;
  exclusion?: ExclusionConfig;
};

const noop = () => {};

const MapItemList = ({
  data,
  identifyIds = null,
  onSelectAsset,
  onSelectWork = noop,
  onSelectUser = noop,
  onHover,
  onCloseIdentify,
  bulkCreate,
  exclusion,
}: MapItemListProps) => {
  const [workOpen, setWorkOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(true);

  const { clusters = [], workOrders = [], users = [], assets = [] } = data;
  const sortedAssets = sortAssets(assets) as typeof assets;
  const total = workOrders.length + users.length + assets.length;

  const enableBulkCreate = Boolean(bulkCreate);
  const isIdentifyMode = R.is(Array, identifyIds) && identifyIds.length > 0;

  const visibleWorkOrders = filterById(workOrders, identifyIds);
  const visibleUsers = filterById(users, identifyIds);
  const visibleAssets = filterById(sortedAssets, identifyIds);

  const excludedCount = exclusion?.excludedCount ?? 0;

  const renderHeader = () => {
    if (isIdentifyMode) {
      return (
        <div className={classes.listHeaderBar}>
          Identify results
          <IconButton onClick={onCloseIdentify} size="small">
            <Icon icon={CloseIcon} />
          </IconButton>
        </div>
      );
    }

    if (!enableBulkCreate) {
      return <div className={classes.listHeaderBar}>{total} items</div>;
    }

    return null;
  };

  const renderExclusionMessage = () => {
    if (excludedCount === 0) {
      return null;
    }

    return (
      <div
        className={classes.exclusionMessage}
        data-cy="mapListExclusionMessage"
      >
        {excludedCount} {pluralize(excludedCount, 'item')} excluded from this
        area. Clear the area to see {excludedCount === 1 ? 'it' : 'them'} again.
      </div>
    );
  };

  const renderEmptyState = () => {
    if (!enableBulkCreate || total > 0 || excludedCount > 0) {
      return null;
    }

    return (
      <NoItemsPane
        text="No items to show"
        subtext={
          clusters.length > 0
            ? 'Please zoom closer on the map to view items'
            : null
        }
      />
    );
  };

  return (
    <div className={classes.container}>
      {renderHeader()}
      <div className={classes.itemList}>
        {renderEmptyState()}
        {!enableBulkCreate && renderExclusionMessage()}

        {enableBulkCreate && (
          <ListHeader
            count={visibleWorkOrders.length}
            label="Work Order"
            open={workOpen}
            onToggle={() => setWorkOpen(prev => !prev)}
            dataCy="mapListWorkOrderHeader"
          />
        )}
        {(workOpen || !enableBulkCreate) &&
          visibleWorkOrders.map(workOrder => (
            <WorkListItem
              key={workOrder.id}
              workOrder={workOrder}
              onSelect={onSelectWork}
              onHover={onHover}
            />
          ))}

        {enableBulkCreate && (
          <ListHeader
            count={visibleUsers.length}
            label="Team Member"
            open={userOpen}
            onToggle={() => setUserOpen(prev => !prev)}
            dataCy="mapListTeamMemberHeader"
          />
        )}
        {(userOpen || !enableBulkCreate) &&
          visibleUsers.map(user => (
            <UserListItem
              key={user.id}
              user={user}
              onSelect={onSelectUser}
              onHover={onHover}
            />
          ))}

        {enableBulkCreate && (
          <ListHeader
            count={visibleAssets.length}
            label="Inventory Item"
            open={assetOpen}
            onToggle={() => setAssetOpen(prev => !prev)}
            actionIcon={
              <NewWorkOrderIcon
                color={
                  visibleAssets.length === 0
                    ? colors.neutral.silver
                    : colors.neutral.gray
                }
              />
            }
            showAction={bulkCreate?.canCreate}
            actionDisabled={visibleAssets.length === 0}
            actionTooltip="Create Work in Bulk"
            onAction={bulkCreate?.onCreate}
            showWhenEmpty={excludedCount > 0}
            dataCy="mapListInventoryItemHeader"
          />
        )}
        {enableBulkCreate && assetOpen && renderExclusionMessage()}
        {(assetOpen || !enableBulkCreate) &&
          visibleAssets.map(asset => (
            <AssetListItem
              key={asset.id}
              asset={asset}
              onSelect={onSelectAsset}
              onHover={onHover}
              onExclude={exclusion?.onExclude}
            />
          ))}
      </div>
    </div>
  );
};

export default MapItemList;
